import {
    useEffect,
    useState } from "react";
import { Link,
    useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import {
    adminDeleteArticle,
    adminListArticles,
    adminMe,
} from "../lib/api";
import { fmtLongDate } from "../lib/dates";
import { AdminHeader } from "../components/AdminNav";

const badgeColor = {
    published: "bg-emerald-100 text-emerald-800",
    draft: "bg-[#E5E1DB] text-[#5C5855]",
    scheduled: "bg-amber-100 text-amber-800",
};

export default function AdminArticles() {
    const [articles, setArticles] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        adminMe().catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    const load = () =>
        adminListArticles()
            .then(setArticles)
            .catch((err) =>
                setError(err?.response?.data?.detail || "Could not load articles")
            );

    useEffect(() => {
        load();
    }, []);

    const onDelete = async (slug, title) => {
        const ok = window.confirm(`Delete "${title}"? This cannot be undone.`);
        if (!ok) return;
        try {
            setBusy(true);
            await adminDeleteArticle(slug);
            await load();
        } catch (err) {
            setError(err?.response?.data?.detail || "Delete failed");
        } finally {
            setBusy(false);
        }
    };

    const linkCls =
        "text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]";

    return (
        <main
            data-testid="page-admin-articles"
            className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16 md:py-24"
        >
            <Seo path="/admin/articles" title="Studio Life — Admin" description="Manage Studio Life articles" noindex />
            <AdminHeader title="Studio Life" />
            <div className="mt-8 flex justify-end">
                <Link
                    to="/admin/articles/new"
                    data-testid="admin-new-article"
                    className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] bg-[#1A1A1A] text-[#F9F8F6] hover:bg-black"
                >
                    + New entry
                </Link>
            </div>

            {error && (
                <p className="mt-8 text-red-700 text-sm" data-testid="admin-articles-error">
                    {error}
                </p>
            )}

            {articles == null ? (
                <p className="mt-12 asc-overline" data-testid="admin-articles-loading">Loading…</p>
            ) : articles.length === 0 ? (
                <p className="mt-12 text-[#5C5855]" data-testid="admin-articles-empty">
                    No entries yet.{" "}
                    <Link to="/admin/articles/new" className="underline">Create one</Link>.
                </p>
            ) : (
                <div className="mt-10 overflow-x-auto">
                    <table
                        data-testid="admin-articles-table"
                        className="w-full text-left text-sm"
                    >
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] border-b border-[#E5E1DB]">
                                <th className="py-3 pr-4 w-16">Cover</th>
                                <th className="py-3 pr-4">Title</th>
                                <th className="py-3 pr-4">Category</th>
                                <th className="py-3 pr-4">Date</th>
                                <th className="py-3 pr-4">Status</th>
                                <th className="py-3 pr-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {articles.map((a) => (
                                <tr
                                    key={a.slug}
                                    data-testid={`admin-article-row-${a.slug}`}
                                    className="border-b border-[#E5E1DB] hover:bg-[#F2EFEB] align-middle"
                                >
                                    <td className="py-3 pr-4">
                                        {a.featured_image && (
                                            <img src={a.featured_image} alt="" className="w-12 h-12 object-cover rounded" />
                                        )}
                                    </td>
                                    <td className="py-3 pr-4 text-[#1A1A1A] font-medium">{a.title}</td>
                                    <td className="py-3 pr-4 text-[#5C5855]">{a.category}</td>
                                    <td className="py-3 pr-4 text-[#5C5855]">{fmtLongDate(a.publish_date)}</td>
                                    <td className="py-3 pr-4">
                                        <span
                                            className={`px-2 py-1 text-[10px] uppercase tracking-[0.18em] ${badgeColor[a.status] || badgeColor.draft}`}
                                        >
                                            {a.status || "draft"}
                                        </span>
                                    </td>
                                    <td className="py-3 pr-4">
                                        <a
                                            href={`/studio-life/${a.slug}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`article-view-${a.slug}`}
                                            className="text-[10px] uppercase tracking-[0.2em] text-[#5C5855] hover:text-[#1A1A1A] mr-4"
                                        >
                                            View
                                        </a>
                                        <Link
                                            to={`/admin/articles/${a.slug}/edit`}
                                            data-testid={`article-edit-${a.slug}`}
                                            className="text-[10px] uppercase tracking-[0.2em] text-[#1A1A1A] mr-4"
                                        >
                                            Edit
                                        </Link>
                                        <button
                                            onClick={() => onDelete(a.slug, a.title)}
                                            disabled={busy}
                                            data-testid={`article-delete-${a.slug}`}
                                            className="text-[10px] uppercase tracking-[0.2em] text-red-700 hover:text-red-900 disabled:opacity-40"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </main>
    );
}
